import { FC } from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { RecentlyEditedIcon, RecentlyCreatedIcon } from '../../../../../components/Icons';

import styles from './IdeasFiltersDrawer.module.scss';
import CustomSwitch from '../../../../../common/CustomSwitch';
import { IdeasFilters } from '../../../../../types/idea.types';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';

interface IdeasFiltersDrawerProps {
  openFilters: boolean;
  toggleFilters: (open: boolean) => void;
  filters: IdeasFilters;
  onFiltersChange: (filters: IdeasFilters) => void;
}

const IdeasFiltersDrawer: FC<IdeasFiltersDrawerProps> = ({ openFilters, toggleFilters, filters, onFiltersChange }) => {
  const isMobile = useIsMobile();

  const handleSortByChange = (field: 'edited_at' | 'created_at') => (checked: boolean) => {
    if (checked) {
      onFiltersChange({
        ...filters,
        sortBy: field,
        sortOrder: 'desc',
      });
    }
  };

  const filterBody = (
    <Box className={styles.filterOptions}>
      <Box className={styles.ideaFilterSwitch}>
        <CustomSwitch
          label="Recently Edited"
          checked={filters.sortBy === 'edited_at'}
          onChange={handleSortByChange('edited_at')}
          icon={<RecentlyEditedIcon size={24} />}
          switchSize="ideas"
        />
      </Box>
      <Box className={styles.ideaFilterSwitch}>
        <CustomSwitch
          label="Recently Created"
          checked={filters.sortBy === 'created_at'}
          onChange={handleSortByChange('created_at')}
          icon={<RecentlyCreatedIcon size={24} />}
          switchSize="ideas"
        />
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={openFilters}
        onClose={() => toggleFilters(false)}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(34, 43, 58, 0.35)' },
          },
        }}
        PaperProps={{
          className: styles.bottomDrawerPaper,
          elevation: 8,
        }}
      >
        <Box className={styles.bottomHeader}>
          <Typography
            variant="h8Bold"
            className={styles.bottomTitle}
          >
            Filters
          </Typography>
          <IconButton
            type="button"
            className={styles.bottomCloseBtn}
            onClick={() => toggleFilters(false)}
            aria-label="Close filters"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.bottomBody}>
          {filterBody}
        </Box>
      </Drawer>
    );
  }

  return (
    <Box className={`${styles.customDrawer} ${openFilters && styles.isShown}`}>
      <Box className={styles.customDrawerHeader}>
        <Typography variant="h5Bold" color="#222B3A">Filters</Typography>
        <CloseIcon
          className={styles.closeIcon}
          onClick={() => toggleFilters(false)}
        />
      </Box>

      <Box className={styles.customDrawerContent}>
        {filterBody}
      </Box>
    </Box>
  );
};

export default IdeasFiltersDrawer;
